import {
    EXAMPLE_APPLICATIONS,
    EXAMPLE_JOB,
    EXAMPLE_OVERVIEW_STATUSES,
    EXAMPLE_SAVED,
    EXAMPLE_WAITING,
    type ExampleJob,
    type ExampleSavedState,
} from "@/lib/example";

/**
 * How Jordan's jobs are tracked in the example: every application as the
 * Applications page lists it, the record of one that went, and the
 * shortlist in named lists. It builds on lib/example.ts, so the jobs and
 * counts agree with Saved, Envoy and Overview: one ready, one that needs
 * Jordan, one being tailored, and one send in the last seven days (to
 * orbital, three days ago). Only what the product records is shown: an
 * application ends sent or skipped, or closes; nothing after the send.
 * Pure data.
 */

/** Where an application stands, as History names it. */
export type ExampleTrackStatus = "ready" | "needs" | "tailoring" | "sent" | "skipped" | "closed";

/** The group the Applications page puts it in. */
export type ExampleTrackGroup = "review" | "preparing" | "history";

export interface ExampleTracked {
    job: ExampleJob;
    status: ExampleTrackStatus;
    /** What the row says after the company and where. */
    line: string;
}

const [, MERIDIAN, ORBITAL] = EXAMPLE_APPLICATIONS;
const LUMEN = EXAMPLE_WAITING[1];
const FABRIKAM = EXAMPLE_SAVED[2];

/** Every application in the example, in the order the page lists them. */
export const EXAMPLE_TRACKED: readonly ExampleTracked[] = [
    { job: EXAMPLE_JOB, status: "ready", line: "tailored this morning" },
    { job: LUMEN.job, status: "needs", line: LUMEN.line },
    { job: MERIDIAN.job, status: "tailoring", line: "from your Top picks list, tailoring now" },
    { job: ORBITAL.job, status: "sent", line: "sent by you, 3 days ago" },
    {
        job: { title: "Platform Engineer II", company: "kestrel", where: "Hybrid, Denver" },
        status: "sent",
        line: "sent by you, 8 days ago",
    },
    {
        job: { title: "Site Reliability Lead", company: "quill", where: "On-site, Seattle" },
        status: "skipped",
        line: "you skipped it: location",
    },
    { job: FABRIKAM.job, status: "closed", line: "the posting closed yesterday" },
];

export const TRACK_GROUP: Record<ExampleTrackStatus, ExampleTrackGroup> = {
    ready: "review",
    needs: "review",
    tailoring: "preparing",
    sent: "history",
    skipped: "history",
    closed: "history",
};

/**
 * The three steps a row's track marks: found (matched or saved),
 * tailored, and how it ended. "done" is reached, "ended" is a skip or a
 * close, "open" is still to come.
 */
export type TrackStep = "done" | "ended" | "open";

export function trackOf(status: ExampleTrackStatus): readonly [TrackStep, TrackStep, TrackStep] {
    switch (status) {
        case "ready":
        case "needs":
            return ["done", "done", "open"];
        case "tailoring":
            return ["done", "open", "open"];
        case "sent":
            return ["done", "done", "done"];
        case "skipped":
        case "closed":
            return ["done", "open", "ended"];
    }
}

/** The counts over the list: the Overview's own, so the two pages agree. */
export const EXAMPLE_TRACK_COUNTS: readonly { label: string; value: number }[] = [
    { label: "Ready", value: EXAMPLE_OVERVIEW_STATUSES.ready },
    { label: "Needs you", value: EXAMPLE_OVERVIEW_STATUSES.needs_you },
    { label: "In progress", value: EXAMPLE_OVERVIEW_STATUSES.tailoring },
    { label: "Sent", value: EXAMPLE_OVERVIEW_STATUSES.applied },
    { label: "Skipped", value: EXAMPLE_OVERVIEW_STATUSES.skipped },
];

/** The record of the send to orbital: what happened, and what it kept. */
export const EXAMPLE_RECORD = {
    job: ORBITAL.job,
    events: [
        { when: "6 days ago", what: "Saved to your Top picks list" },
        { when: "5 days ago", what: "Tailored: 11 of 13 keywords covered" },
        { when: "3 days ago", what: "Sent by you, with this resume" },
    ],
    /** The tailored resume that went, as History prints it: size and short hash. */
    resumeMeta: "84 KB · 3f9a1c",
} as const;

/** A saved job's state on the Saved page: the posting open, tailoring, closed, or marked applied. */
export type ExampleShortlistState = ExampleSavedState | "applied";

export interface ExampleShortlisted {
    job: ExampleJob;
    state: ExampleShortlistState;
    saved: string;
    list: string;
}

/** Jordan's lists, in the order the Saved page shows them. */
export const EXAMPLE_LISTS: readonly string[] = ["Top picks", "Maybe later"];

/**
 * Jordan's shortlist, the newest saved first: the three of lib/example.ts
 * in their lists, and orbital, saved first and since sent.
 */
export const EXAMPLE_SHORTLIST: readonly ExampleShortlisted[] = [
    { ...EXAMPLE_SAVED[0], list: "Maybe later" },
    { ...EXAMPLE_SAVED[1], list: "Top picks" },
    { ...FABRIKAM, list: "Top picks" },
    { job: ORBITAL.job, state: "applied", saved: "saved 6 days ago", list: "Top picks" },
];

/** How many saved jobs each list holds. */
export function listCounts(
    shortlist: readonly ExampleShortlisted[] = EXAMPLE_SHORTLIST,
): { name: string; count: number }[] {
    return EXAMPLE_LISTS.map((name) => ({
        name,
        count: shortlist.filter((entry) => entry.list === name).length,
    }));
}
